import { useCallback, useEffect, useRef, useState } from 'react';
import { RoomLevelIssues } from 'types/room';
import { getRoomIssues, PayloadRoomIssues, UpdateRoomIssues } from 'services/inspections';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormRoomLevelIssuesSchema } from 'utils/validate';
import { ListOptions } from '../RoomReportViewPhotoPage/hook';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { appendDynamicOptionForPayload, appendDynamicSingleForPayload } from 'utils/convertData';
import { useFlash } from 'contexts/flash';
import { useRoomContext } from 'contexts/RoomContext';
type FormRoomLevelIssues = {
  type: string;
  typeOther?: string;
  description: string;
  actionBy?: string | null;
  actionByDate?: string | null;
  validated: string;
  status: string;
  resolutionDate?: string;
  file?: File;
} & Record<string, unknown>;
export const useInjectRoomLevelIssuesPage = () => {
  const { submitId, id } = useParams();
  const { setFlash } = useFlash();
  const [roomIssues, setRoomIssues] = useState<RoomLevelIssues>();
  const [options, setOptions] = useState<ListOptions>();
  const [statusOptions, setStatusOptions] = useState<{ value: string; label: string }[]>([
    { value: 'ACTIVE', label: 'Open' },
    { value: 'RESOLVED', label: 'Resolved' }
  ]);

  const {
    handleSubmit,
    register,
    control,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm<FormRoomLevelIssues>({
    resolver: yupResolver(FormRoomLevelIssuesSchema)
  });
  const [isArchived, setIsArchived] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const { systemDropdownValues } = useRoomContext();

  const handleGetOptions = useCallback(async () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dataOption = systemDropdownValues as any;
    setOptions(dataOption);

    // Try to get status options from system dropdown values

    if (dataOption.status && dataOption.status.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const statusOpts = dataOption?.status?.map((item: any) => ({
        value: item.value,
        label: item.value
      }));
      setStatusOptions(statusOpts);
    }
  }, [systemDropdownValues]);

  useEffect(() => {
    handleGetOptions();
  }, [handleGetOptions]);

  const handleGetIssues = useCallback(async () => {
    if (!submitId || !id) return;
    const data = (await getRoomIssues({ submitId, roomIssuesId: id })) as {
      roomIssue: RoomLevelIssues;
    };

    setIsArchived(!!data.roomIssue.archivedAt);

    const roomLevelIssueOptions: Record<string, string> = {};

    data?.roomIssue?.options?.forEach((roomIssueOption) => {
      options?.forEach((dropdownValue) => {
        const hasAssetType = dropdownValue.id.toString() === roomIssueOption.type;

        if (hasAssetType) {
          const index = dropdownValue.dropdownValues.some(
            (dropdown) => dropdown.value === roomIssueOption.value
          );

          roomLevelIssueOptions[`option_${roomIssueOption?.type}`] = index
            ? roomIssueOption?.value
            : 'OTHER';
          roomLevelIssueOptions[`option_${roomIssueOption?.type}_name`] = roomIssueOption?.value;
        }
      });
    });

    reset({
      type: data.roomIssue.type,
      description: data.roomIssue.description,
      actionBy: !data.roomIssue.actionBy?.startsWith('other_') ? data.roomIssue.actionBy : 'OTHER',
      actionByName: !data.roomIssue.actionBy?.startsWith('other_')
        ? ''
        : data.roomIssue.actionBy?.replace('other_', ''),
      actionByDate: data.roomIssue.actionByDate,
      validated: data.roomIssue.validated ? 'Yes' : 'No',
      status: data.roomIssue.status,
      resolutionDate: data.roomIssue.resolutionDate,
      ...roomLevelIssueOptions
    });
    setRoomIssues(data.roomIssue);
  }, [id, options, reset, submitId]);
  useEffect(() => {
    handleGetIssues();
  }, [handleGetIssues]);

  const handleSubmitForm = useCallback(
    async (data: FormRoomLevelIssues) => {
      if (!submitId || !id) return;

      const payload: PayloadRoomIssues = {
        type: data.type === 'Other' ? data.typeOther || '' : data.type,
        description: data.description,
        actionBy: data.actionBy === 'OTHER' ? `other_${data.actionByName}` : data.actionBy || '',
        actionByDate: data.actionByDate || '',
        resolutionDate: data.status === 'ACTIVE' ? null : data.resolutionDate,
        options: JSON.stringify(appendDynamicOptionForPayload(data)),
        singleFields: JSON.stringify(appendDynamicSingleForPayload(data)),
        validated: data.validated === 'Yes',
        status: data.status,
        file: data.file
      };
      await UpdateRoomIssues({ submitId, roomIssuesId: id, payload });
      setValue(
        'resolutionDate',
        data.status === 'RESOLVED' && !data.resolutionDate ? new Date().toISOString() : ''
      );
      setFlash({ type: 'success', message: 'Update room issue successful!' });
    },
    [submitId, id, setValue, setFlash]
  );

  const { asyncCallback: asyncHandleSubmitForm } = useAsyncCallback(handleSubmitForm, []);

  return {
    roomIssues,
    handleSubmit,
    register,
    control,
    reset,
    setValue,
    watch,
    errors,
    options,
    statusOptions,
    isArchived,
    inputRef,
    handleSubmitForm: asyncHandleSubmitForm,
    onDelete: useCallback(async () => {
      let confirmDeteleIssue = true;
      if (!isArchived) confirmDeteleIssue = window.confirm('Archive this location level issue?');
      else confirmDeteleIssue = window.confirm('Unarchive this location level issue?');
      if (confirmDeteleIssue && submitId && id) {
        await UpdateRoomIssues({ submitId, roomIssuesId: id, payload: { archived: !isArchived } });
        await handleGetIssues();
        setFlash({
          type: 'success',
          message: !isArchived ? 'Room issue archived.' : 'Room issue unarchived.'
        });
      }
    }, [id, setFlash, submitId, handleGetIssues, isArchived]),
    onChangePhoto: useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.[0]) {
          setValue('file', e.target.files[0]);
        }
      },
      [setValue]
    )
  };
};
